import { initializeApp, getApps, type FirebaseApp } from 'firebase/app';
import { collection, getDocs, getFirestore, query } from 'firebase/firestore/lite';

export type BlogPost = {
  id: string;
  title: string;
  slug: string;
  date: string;
  author: string;
  status?: string;
  featured?: boolean;
  tags?: string[];
  sites?: string[];
  featured_image?: string;
  excerpt?: string;
  content: string;
  metaDescription?: string;
  metaKeywords?: string[];
  authorBio?: string;
  authorLinks?: {
    linkedin?: string;
    twitter?: string;
    email?: string;
  };
  imageAlt?: string;
};

type BlogFetchOptions = {
  siteKey?: string;
};

const BLOG_APP_NAME = 'tagitBlogApp';

const BLOG_ENV = {
  VITE_BLOG_FIREBASE_API_KEY: import.meta.env.VITE_BLOG_FIREBASE_API_KEY as string | undefined,
  VITE_BLOG_FIREBASE_AUTH_DOMAIN: import.meta.env.VITE_BLOG_FIREBASE_AUTH_DOMAIN as string | undefined,
  VITE_BLOG_FIREBASE_PROJECT_ID: import.meta.env.VITE_BLOG_FIREBASE_PROJECT_ID as string | undefined,
  VITE_BLOG_FIREBASE_STORAGE_BUCKET: import.meta.env.VITE_BLOG_FIREBASE_STORAGE_BUCKET as string | undefined,
  VITE_BLOG_FIREBASE_MESSAGING_SENDER_ID: import.meta.env.VITE_BLOG_FIREBASE_MESSAGING_SENDER_ID as string | undefined,
  VITE_BLOG_FIREBASE_APP_ID: import.meta.env.VITE_BLOG_FIREBASE_APP_ID as string | undefined,
  VITE_BLOG_FIREBASE_MEASUREMENT_ID: import.meta.env.VITE_BLOG_FIREBASE_MEASUREMENT_ID as string | undefined,
};

function getEnv(name: keyof typeof BLOG_ENV) {
  return BLOG_ENV[name];
}

function initBlogApp(): FirebaseApp {
  const existing = getApps().find((app) => app.name === BLOG_APP_NAME);
  if (existing) return existing;

  const config = {
    apiKey: getEnv('VITE_BLOG_FIREBASE_API_KEY'),
    authDomain: getEnv('VITE_BLOG_FIREBASE_AUTH_DOMAIN'),
    projectId: getEnv('VITE_BLOG_FIREBASE_PROJECT_ID'),
    storageBucket: getEnv('VITE_BLOG_FIREBASE_STORAGE_BUCKET'),
    messagingSenderId: getEnv('VITE_BLOG_FIREBASE_MESSAGING_SENDER_ID'),
    appId: getEnv('VITE_BLOG_FIREBASE_APP_ID'),
    measurementId: getEnv('VITE_BLOG_FIREBASE_MEASUREMENT_ID'),
  } as const;

  if (!config.projectId) {
    throw new Error('Missing blog Firebase config (VITE_BLOG_FIREBASE_PROJECT_ID).');
  }

  return initializeApp(config, BLOG_APP_NAME);
}

function stripHtmlToText(html: string) {
  return html
    .replace(/<script[\s\S]*?>[\s\S]*?<\/script>/gi, ' ')
    .replace(/<style[\s\S]*?>[\s\S]*?<\/style>/gi, ' ')
    .replace(/<[^>]+>/g, ' ')
    .replace(/\s+/g, ' ')
    .trim();
}

function toBlogPost(docId: string, data: Record<string, unknown>): BlogPost {
  return {
    id: docId,
    title: String(data.title ?? ''),
    slug: String(data.slug ?? ''),
    date: String(data.date ?? ''),
    author: String(data.author ?? ''),
    status: typeof data.status === 'string' ? data.status : undefined,
    featured: typeof data.featured === 'boolean' ? data.featured : undefined,
    tags: Array.isArray(data.tags) ? (data.tags as string[]).filter(Boolean) : undefined,
    sites: Array.isArray(data.sites) ? (data.sites as string[]).filter(Boolean) : undefined,
    featured_image: typeof data.featured_image === 'string' ? data.featured_image : undefined,
    excerpt: typeof data.excerpt === 'string' ? data.excerpt : undefined,
    content: typeof data.content === 'string' ? data.content : '',
    metaDescription: typeof data.metaDescription === 'string' ? data.metaDescription : undefined,
    metaKeywords: Array.isArray(data.metaKeywords)
      ? (data.metaKeywords as string[]).filter(Boolean)
      : undefined,
    authorBio: typeof data.authorBio === 'string' ? data.authorBio : undefined,
    authorLinks: typeof data.authorLinks === 'object' && data.authorLinks
      ? (data.authorLinks as any)
      : undefined,
    imageAlt: typeof data.imageAlt === 'string' ? data.imageAlt : undefined,
  };
}

let cachedAllPosts: { posts: BlogPost[]; fetchedAt: number } | null = null;

export async function fetchBlogPosts({ siteKey = 'tagIt' }: BlogFetchOptions = {}) {
  const now = Date.now();
  if (cachedAllPosts && now - cachedAllPosts.fetchedAt < 60_000) {
    return cachedAllPosts.posts.filter((p) => (Array.isArray(p.sites) ? p.sites.includes(siteKey) : false));
  }

  const app = initBlogApp();
  const db = getFirestore(app);

  const snap = await getDocs(query(collection(db, 'blogs')));
  const posts = snap.docs
    .map((d) => toBlogPost(d.id, d.data() as any))
    .filter((p) => p.slug && p.title && p.content);

  cachedAllPosts = { posts, fetchedAt: now };

  return posts
    .filter((p) => (Array.isArray(p.sites) ? p.sites.includes(siteKey) : false))
    .sort((a, b) => (b.date ?? '').localeCompare(a.date ?? ''));
}

export async function fetchBlogPostBySlug(slug: string, { siteKey = 'tagIt' }: BlogFetchOptions = {}) {
  const posts = await fetchBlogPosts({ siteKey });
  return posts.find((p) => p.slug === slug) ?? null;
}

export function estimateReadTimeMinutes(html: string) {
  const text = stripHtmlToText(html);
  if (!text) return 1;
  const words = text.split(/\s+/).filter(Boolean).length;
  return Math.max(1, Math.round(words / 200));
}

export function excerptFromPost(post: BlogPost, maxChars = 150) {
  if (post.excerpt && post.excerpt.trim()) return post.excerpt.trim();
  const text = stripHtmlToText(post.content);
  if (text.length <= maxChars) return text;
  return `${text.slice(0, maxChars).trim()}…`;
}

export function formatBlogDate(dateIso: string) {
  const d = new Date(dateIso);
  if (Number.isNaN(d.getTime())) return dateIso;
  return new Intl.DateTimeFormat('en-US', { year: 'numeric', month: 'long', day: 'numeric' }).format(d);
}

